import type { Database, Json } from '../../database.types';
import type { PropertyType } from '../deals';
import { resolveDealSlug } from '../../slug-generator';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

export type AdminDeal = Database['public']['Tables']['deals']['Row'];
export type AdminDealUpdate = Database['public']['Tables']['deals']['Update'];
export type AdminDealStatus = Database['public']['Enums']['deal_status'];

/**
 * Subset of `deals` columns the back-office form is allowed to PATCH via
 * {@link updateAdminDeal}. status / slug / publication_date / sold_at are
 * driven by the FSM transition path ({@link transitionDealStatus}) and
 * MUST NOT be present here — putting status in here would let an admin
 * bypass the FSM trigger by routing through plain UPDATE.
 *
 * is_featured is in {@link toggleDealFeatured}, also separated for
 * clarity (audit trigger picks up on featured_at via a dedicated
 * trigger, see 20260428040000_audit_log.sql §deals_log_featured_toggle).
 */
export type AdminDealEditablePatch = Pick<
  AdminDealUpdate,
  | 'title'
  | 'description'
  | 'zone'
  | 'community'
  | 'building'
  | 'zone_key'
  | 'community_key'
  | 'building_key'
  | 'latitude'
  | 'longitude'
  | 'type'
  | 'property_type'
  | 'area_sqft'
  | 'bedrooms'
  | 'bathrooms'
  | 'floor'
  | 'view'
  | 'handover'
  | 'asking_price'
  | 'reference_price'
  | 'original_price'
  | 'amount_paid'
  | 'meta_title'
  | 'meta_description'
  | 'expiration_date'
  | 'internal_notes'
  | 'advisor_id'
>;

/**
 * Load a single deal by id for the admin edit form. Returns `null`
 * when no row matches (or RLS hides it). Throws on every other error.
 *
 * RLS: relies on `deals_select` policy from 003_deals.sql §14.2 — admin
 * and super_admin see every status, including soft-deleted rows.
 */
export async function getAdminDealById(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal | null> {
  const { data, error } = await client.from('deals').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data ?? null) as AdminDeal | null;
}

/**
 * Patch the editable subset of a deal. status / slug / publication_date
 * are intentionally excluded — those go through {@link transitionDealStatus}.
 *
 * RLS: relies on `deals_update` (admin or super_admin). The trigger
 * `deals_protect_asking_price_post_published_trg` will also raise if a
 * non-super_admin tries to amend asking_price on a published deal —
 * that error bubbles up unchanged, the UI surfaces it as a toast.
 */
export async function updateAdminDeal(
  id: string,
  patch: AdminDealEditablePatch,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const { data, error } = await client
    .from('deals')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AdminDeal;
}

export type TransitionDealStatusParams = {
  dealId: string;
  toStatus: AdminDealStatus;
  /** Required when toStatus = 'refused'. Min 20 chars (Zod), 10 chars (DB CHECK). */
  motif?: string | null;
  /** Optional fresh slug — typically supplied on validating → published. */
  slug?: string | null;
};

/**
 * Transition a deal from one status to another. Routed through the
 * `transition_deal_status` RPC (SECURITY INVOKER) so the
 * `deals.refusal_reason` GUC reaches the AFTER UPDATE history trigger
 * inside the same transaction. Without that, refused transitions raise
 * `deal_status_history_refused_reason` (CHECK 23514).
 *
 * The FSM trigger from 003_deals.sql §12 enforces the role/transition
 * matrix: draft→validating (admin/super_admin), validating→published
 * (super_admin), validating→refused (super_admin), published→archived,
 * published→sold (admin/super_admin). Any other transition raises
 * `check_violation`.
 */
export async function transitionDealStatus(
  params: TransitionDealStatusParams,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const { data, error } = await client.rpc('transition_deal_status', {
    p_deal_id: params.dealId,
    p_to_status: params.toStatus,
    p_motif: params.motif ?? undefined,
    p_slug: params.slug ?? undefined,
  });
  if (error) throw error;
  return data as AdminDeal;
}

/**
 * Resolve the publish slug for `validating → published` then delegate
 * to {@link transitionDealStatus}. Pulls the deal first so we don't
 * trust client-side Zod values as the canonical metadata.
 */
export async function publishDeal(
  dealId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const current = await getAdminDealById(dealId, client);
  if (!current) {
    throw new Error(`Deal ${dealId} not found`);
  }
  const slug = await resolveDealSlug(
    {
      zone: current.zone,
      building: current.building,
      community: current.community,
      bedrooms: current.bedrooms,
      discountPct: current.discount_pct,
    },
    { dealId, client },
  );
  return transitionDealStatus({ dealId, toStatus: 'published', slug }, client);
}

/**
 * Republish an archived deal on its existing row (Modele V1.2 §10.1, D3).
 *
 * Deliberately NOT routed through {@link transitionDealStatus}: the generic
 * RPC issues an unconditional UPDATE, and the FSM trigger short-circuits on
 * `NEW.status = OLD.status`, so a double-click would report two successes —
 * the second one a no-op on an already-published deal. `republish_deal`
 * locks the row FOR UPDATE and raises 55000 unless the current status is
 * exactly `archived`.
 *
 * No `p_slug` equivalent exists on purpose: the slug (and therefore the
 * public URL) and `publication_date` are preserved, so a restored listing
 * keeps its canonical URL and its original catalogue position.
 *
 * Authorisation lives in the FSM trigger (super_admin only, 42501) per
 * ADR-030 — the RPC adds no role check of its own.
 */
export async function republishDeal(
  dealId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const { data, error } = await client.rpc('republish_deal', { p_deal_id: dealId });
  if (error) throw error;
  return data as AdminDeal;
}

/**
 * True when a deal carries an `expiration_date` already in the past.
 *
 * Republishing such a deal succeeds, but the nightly `auto_expire_deals`
 * CRON (published + expiration_date <= NOW()) flips it straight back to
 * `expired`. No expiry rule (duration, renewal, extension) exists in any
 * source document, so the BO warns instead of silently rewriting the date
 * — see ADR-059.
 */
export function isExpirationPast(expirationDate: string | null, now: Date = new Date()): boolean {
  if (!expirationDate) return false;
  const parsed = Date.parse(expirationDate);
  return Number.isFinite(parsed) && parsed <= now.getTime();
}

/**
 * Toggle the home-carousel `is_featured` flag. The
 * `deals_maintain_featured_at_trg` trigger maintains `featured_at`
 * automatically; the audit trigger `deals_log_featured_toggle_trg`
 * records the change (Lot 5a).
 *
 * RLS: super_admin only. RPC alternative not needed — plain UPDATE
 * on a column with no FSM gating.
 */
export async function toggleDealFeatured(
  id: string,
  on: boolean,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const { data, error } = await client
    .from('deals')
    .update({ is_featured: on })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AdminDeal;
}

/**
 * Soft-delete a deal. The trigger `deals_protect_soft_delete_trg`
 * limits this to super_admin (Matrice §M10). The deal vanishes from
 * the public catalogue and from admin lists unless an "Include
 * deleted" filter explicitly opts in.
 */
export async function softDeleteDeal(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client
    .from('deals')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Restore a previously soft-deleted deal. super_admin only (same trigger). */
export async function restoreDeal(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const { data, error } = await client
    .from('deals')
    .update({ deleted_at: null })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AdminDeal;
}

/**
 * BO-2 — payload shape for {@link createAdminDeal}. Mirrors the editable
 * subset enforced by `adminDealCreateSchema` (Type + Property + Price) plus
 * the optional notes / SEO fields the RPC silently accepts (null when
 * absent). `created_by` is intentionally not part of the shape: the RPC
 * forces it to `auth.uid()` server-side (D2 — front cannot spoof author).
 */
export type AdminDealCreatePayload = {
  type: 'ready' | 'off_plan';
  title: string;
  description: string;
  zone: string;
  community: string;
  building: string | null;
  // ADR-055 — canonical DLD keys persisted on combobox selection; NULL when
  // the label is free text (the engine resolves text/aliases at compute time).
  zone_key: string | null;
  community_key: string | null;
  building_key: string | null;
  latitude: number | null;
  longitude: number | null;
  property_type: PropertyType;
  area_sqft: number;
  bedrooms: number;
  bathrooms: number | null;
  floor: number | null;
  view: string | null;
  handover: string | null;
  asking_price: number;
  original_price: number | null;
  amount_paid: number | null;
  expiration_date?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  internal_notes?: string | null;
  advisor_id?: string | null;
};

/**
 * Create a deal Draft from scratch. Routed through the
 * `create_admin_deal` RPC (SECURITY INVOKER) which:
 *   - assigns a provisional slug 'draft-{8 hex}' (D1).
 *   - forces created_by := auth.uid() (D2).
 *   - lets RLS deals_insert + DB CHECKs gate the call (admin / super_admin
 *     only; description ≥ 50 chars; off_plan ⇒ original_price + amount_paid).
 *
 * Returns the freshly created `deals` row including the auto-assigned
 * `reference_id` (format DDM-YYYY-NNNNNN). The status defaults to 'draft'
 * — the BO-1 editor takes over from there for FSM transitions.
 */
export async function createAdminDeal(
  payload: AdminDealCreatePayload,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDeal> {
  const { data, error } = await client.rpc('create_admin_deal', {
    p_payload: payload as unknown as Json,
  });
  if (error) throw error;
  return data as AdminDeal;
}
